import { useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, Search, Minus, Plus, Send, ShoppingBag, UtensilsCrossed } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { usePoll } from "@/hooks/usePoll";
import { api, errMsg, rp, imgSrc } from "@/lib/api";
import { StageBadge, Empty } from "@/components/common";
import { ItemStatus } from "@/components/BillItems";

function MenuCard({ m, qty, onAdd }) {
  return (
    <button data-testid={`menu-card-${m.id}`} onClick={onAdd} className="gm-rise relative flex flex-col overflow-hidden rounded-2xl border border-stone-200 bg-white text-left shadow-sm transition-colors hover:border-orange-300 active:scale-[0.97]">
      {m.image_url ? <img src={imgSrc(m.image_url)} alt={m.name} className="h-24 w-full object-cover" /> : <div className="grid h-24 place-items-center bg-gradient-to-br from-orange-50 to-amber-100"><UtensilsCrossed className="h-7 w-7 text-orange-300" /></div>}
      <div className="p-3">
        <p className="line-clamp-2 text-sm font-semibold leading-snug">{m.name}</p>
        <p className="mt-1 font-mono text-sm font-bold text-orange-700">{rp(m.price)}</p>
      </div>
      {qty > 0 && <span className="absolute right-2 top-2 grid h-7 min-w-7 place-items-center rounded-full bg-orange-600 px-2 text-xs font-bold text-white">{qty}</span>}
    </button>
  );
}

function CartLine({ line, onQty, onNote }) {
  return (
    <div className="border-b border-stone-100 py-3" data-testid={`cart-line-${line.menu_id}`}>
      <div className="flex items-center gap-3">
        <div className="min-w-0 flex-1"><p className="truncate font-semibold">{line.name}</p><p className="font-mono text-xs text-zinc-500">{rp(line.price * line.qty)}</p></div>
        <div className="flex items-center gap-2">
          <Button size="icon" variant="outline" className="h-8 w-8" data-testid={`cart-minus-${line.menu_id}`} onClick={() => onQty(-1)}><Minus className="h-3.5 w-3.5" /></Button>
          <span className="w-6 text-center font-mono font-bold" data-testid={`cart-qty-${line.menu_id}`}>{line.qty}</span>
          <Button size="icon" variant="outline" className="h-8 w-8" data-testid={`cart-plus-${line.menu_id}`} onClick={() => onQty(1)}><Plus className="h-3.5 w-3.5" /></Button>
        </div>
      </div>
      <Input data-testid={`cart-note-${line.menu_id}`} value={line.note} onChange={(e) => onNote(e.target.value)} placeholder="Catatan (opsional): pedas, tanpa es..." className="mt-2 h-8 text-xs" />
    </div>
  );
}

export default function WaiterOrder() {
  const { id } = useParams();
  const navigate = useNavigate();
  const bill = usePoll(`/bills/${id}`, 8000);
  const menus = (usePoll("/menus", 0, { available: true }).data || []);
  const cats = usePoll("/categories", 0).data || [];
  const [cat, setCat] = useState("all");
  const [q, setQ] = useState("");
  const [cart, setCart] = useState({});
  const [cartOpen, setCartOpen] = useState(false);
  const [busy, setBusy] = useState(false);

  const list = useMemo(() => menus.filter((m) => (cat === "all" || m.category_id === cat) && m.name.toLowerCase().includes(q.toLowerCase())), [menus, cat, q]);
  const lines = Object.values(cart);
  const count = lines.reduce((n, l) => n + l.qty, 0);
  const sum = lines.reduce((n, l) => n + l.qty * l.price, 0);
  const b = bill.data;
  const locked = b && (b.status !== "active" || b.order_closed);

  const add = (m) => setCart((c) => ({ ...c, [m.id]: { menu_id: m.id, name: m.name, price: m.price, qty: (c[m.id]?.qty || 0) + 1, note: c[m.id]?.note || "" } }));
  const setQty = (mid, d) => setCart((c) => {
    const qty = c[mid].qty + d;
    const next = { ...c };
    if (qty <= 0) delete next[mid]; else next[mid] = { ...c[mid], qty };
    return next;
  });
  const setNote = (mid, note) => setCart((c) => ({ ...c, [mid]: { ...c[mid], note } }));

  const submit = async () => {
    setBusy(true);
    try {
      const r = await api.post(`/bills/${id}/order`, { items: lines.map(({ menu_id, qty, note }) => ({ menu_id, qty, note })) });
      bill.setData(r.data);
      setCart({});
      setCartOpen(false);
      toast.success("Order berhasil dikirim ke Dapur!");
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };

  if (!b) return <p className="text-sm text-zinc-500">Memuat bill...</p>;

  return (
    <div className="pb-28">
      <button data-testid="back-to-bills-btn" onClick={() => navigate("/waiter")} className="mb-4 flex items-center gap-1 text-sm text-zinc-500 hover:text-orange-700"><ArrowLeft className="h-4 w-4" /> Daftar bill</button>
      <div className="mb-5 flex flex-wrap items-center gap-3 rounded-2xl bg-[#1C1410] p-4 text-white">
        <div className="flex-1">
          <p className="text-[11px] uppercase tracking-widest text-orange-400">Kode Bill</p>
          <p className="font-mono text-xl font-bold" data-testid="order-bill-code">{b.code}</p>
          <p className="text-sm text-stone-300">{b.customer_name} · {b.table_name}</p>
        </div>
        <StageBadge stage={b.stage} testId="order-bill-stage" />
      </div>
      {locked && <p data-testid="order-locked-msg" className="mb-4 rounded-xl bg-amber-50 px-4 py-3 text-sm text-amber-800">Bill ini sudah ditutup kasir, tidak bisa menambah order.</p>}

      {b.items.length > 0 && (
        <details className="mb-5 rounded-2xl border border-stone-200 bg-white p-4" data-testid="ordered-items">
          <summary className="cursor-pointer text-sm font-semibold">Sudah dipesan ({b.items.reduce((n, i) => n + i.qty, 0)} item · {rp(b.totals.total)})</summary>
          <div className="mt-3 divide-y divide-stone-100">
            {b.items.map((i) => (
              <div key={i.id} className="flex items-center justify-between py-2 text-sm"><span>{i.qty}x {i.name}{i.note && <em className="text-zinc-400"> ({i.note})</em>}</span><ItemStatus item={i} /></div>
            ))}
          </div>
        </details>
      )}

      <div className="sticky top-0 z-10 -mx-4 mb-4 space-y-3 bg-stone-50/95 px-4 py-2 backdrop-blur md:-mx-8 md:px-8">
        <div className="relative"><Search className="absolute left-3 top-3 h-4 w-4 text-zinc-400" /><Input data-testid="order-menu-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Cari menu..." className="h-10 bg-white pl-9" /></div>
        <div className="flex gap-2 overflow-x-auto pb-1">
          {[{ id: "all", name: "Semua" }, ...cats].map((c) => (
            <button key={c.id} data-testid={`order-cat-${c.id}`} onClick={() => setCat(c.id)} className={`whitespace-nowrap rounded-full px-4 py-1.5 text-sm font-semibold transition-colors ${cat === c.id ? "bg-orange-600 text-white" : "border border-stone-200 bg-white text-zinc-600 hover:bg-orange-50"}`}>{c.name}</button>
          ))}
        </div>
      </div>
      {list.length === 0 ? <Empty icon={UtensilsCrossed} text="Menu tidak ditemukan" /> : (
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5" data-testid="order-menu-grid">
          {list.map((m) => <MenuCard key={m.id} m={m} qty={cart[m.id]?.qty || 0} onAdd={() => !locked && add(m)} />)}
        </div>
      )}

      {count > 0 && (
        <div className="fixed inset-x-0 bottom-0 z-30 p-4 lg:left-64">
          <button data-testid="open-cart-btn" onClick={() => setCartOpen(true)} className="mx-auto flex w-full max-w-xl items-center gap-3 rounded-2xl bg-orange-600 px-5 py-4 text-white shadow-xl shadow-orange-900/20 active:scale-[0.98]">
            <ShoppingBag className="h-5 w-5" /><span className="font-semibold" data-testid="cart-count">{count} item</span>
            <span className="ml-auto font-mono font-bold">{rp(sum)}</span>
          </button>
        </div>
      )}
      <Sheet open={cartOpen} onOpenChange={setCartOpen}>
        <SheetContent side="bottom" className="mx-auto max-h-[85vh] max-w-xl overflow-y-auto rounded-t-3xl" data-testid="cart-sheet">
          <SheetHeader><SheetTitle>Order untuk {b.code}</SheetTitle></SheetHeader>
          <div className="mt-2">{lines.map((l) => <CartLine key={l.menu_id} line={l} onQty={(d) => setQty(l.menu_id, d)} onNote={(v) => setNote(l.menu_id, v)} />)}</div>
          <div className="mt-4 flex items-center justify-between text-sm"><span>Subtotal</span><span className="font-mono font-bold">{rp(sum)}</span></div>
          <Button data-testid="submit-order-btn" disabled={busy || !count} onClick={submit} className="mt-4 h-12 w-full rounded-xl bg-orange-600 text-base hover:bg-orange-700 active:scale-95">
            <Send className="mr-2 h-4 w-4" />{busy ? "Mengirim..." : "Order (Kirim ke Dapur)"}
          </Button>
        </SheetContent>
      </Sheet>
    </div>
  );
}
